/*eslint-disable no-unused-vars */
/*eslint-disable react-hooks/exhaustive-deps*/
import React, { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Button, Col, Input, Row, Table } from 'reactstrap';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

import { setLoadingOverlay } from '../../store/slices/loadingOverlaySlice';
import LocalstorageService from '../../helpers/localstorage-service';
import { CompanyId } from '../../constants';
import profitabilityService from '../../services/profitability';
import NoRecords from '../../components/Table/NoRecords';

const MONTHS_HE = ['', 'ינואר', 'פברואר', 'מרץ', 'אפריל', 'מאי', 'יוני',
  'יולי', 'אוגוסט', 'ספטמבר', 'אוקטובר', 'נובמבר', 'דצמבר'];

const fmt = (v) => (v == null ? '0' : Number(v).toLocaleString('he-IL'));

const EmployeeSalary = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [setPageTitle, setCrumbItem] = useOutletContext();

  const companyId = LocalstorageService.getItem(CompanyId);
  const now = new Date();

  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear]   = useState(now.getFullYear());
  const [rows, setRows]   = useState([]);
  const [editRows, setEditRows] = useState({});
  const [saving, setSaving]     = useState(false);

  useEffect(() => {
    setPageTitle(t('PROFITABILITY.SALARY_PAGE.PAGE_TITLE'));
    setCrumbItem([
      { title: t('PROFITABILITY.SALARY_PAGE.CRUMB_PARENT'), link: '#' },
      { title: t('PROFITABILITY.SALARY_PAGE.CRUMB_ITEM'), link: '#' },
    ]);
  }, []);

  const fetchSalaries = async () => {
    dispatch(setLoadingOverlay(true));
    try {
      const res = await profitabilityService.getSalaries({ companyId, month, year });
      const data = res.data?.dataList || [];
      setRows(data);
      // טען ערכים לעריכה
      const init = {};
      data.forEach(r => {
        init[r.employeeId] = {
          baseSalary:  r.baseSalary  || 0,
          bonusAmount: r.bonusAmount || 0,
          bonusNote:   r.bonusNote   || '',
        };
      });
      setEditRows(init);
    } catch {
      toast.error(t('PROFITABILITY.SALARY_PAGE.TOAST_ERROR_LOAD'));
    } finally {
      dispatch(setLoadingOverlay(false));
    }
  };

  useEffect(() => { fetchSalaries(); }, [month, year]);

  const handleChange = (empId, field, value) => {
    setEditRows(prev => ({
      ...prev,
      [empId]: { ...prev[empId], [field]: value },
    }));
  };

  const handleSaveAll = async () => {
    setSaving(true);
    try {
      await Promise.all(
        rows.map(r =>
          profitabilityService.saveSalary({
            companyId,
            employeeId: r.employeeId,
            month, year,
            baseSalary:  parseFloat(editRows[r.employeeId]?.baseSalary  || 0),
            bonusAmount: parseFloat(editRows[r.employeeId]?.bonusAmount || 0),
            bonusNote:   editRows[r.employeeId]?.bonusNote || '',
          })
        )
      );
      toast.success(t('PROFITABILITY.SALARY_PAGE.TOAST_SAVED'));
      fetchSalaries();
    } catch {
      toast.error(t('PROFITABILITY.SALARY_PAGE.TOAST_ERROR_SAVE'));
    } finally {
      setSaving(false);
    }
  };

  const totalSalary = rows.reduce((s, r) => {
    const ed = editRows[r.employeeId] || {};
    return s + parseFloat(ed.baseSalary || 0) + parseFloat(ed.bonusAmount || 0);
  }, 0);

  const yearOptions = [];
  for (let y = now.getFullYear(); y >= now.getFullYear() - 5; y--) yearOptions.push(y);

  return (
    <React.Fragment>
      <Row className="mb-3 align-items-end">
        <Col xs="auto">
          <label className="form-label mb-1">{t('PROFITABILITY.COMMON.MONTH')}</label>
          <select className="form-select" value={month} onChange={e => setMonth(+e.target.value)}>
            {MONTHS_HE.slice(1).map((m, i) => (
              <option key={i + 1} value={i + 1}>{m}</option>
            ))}
          </select>
        </Col>
        <Col xs="auto">
          <label className="form-label mb-1">{t('PROFITABILITY.COMMON.YEAR')}</label>
          <select className="form-select" value={year} onChange={e => setYear(+e.target.value)}>
            {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </Col>
      </Row>

      {rows.length === 0 ? <NoRecords /> : (
        <>
          <div className="table-responsive mb-3">
            <Table bordered hover size="sm">
              <thead className="table-dark">
                <tr>
                  <th>{t('PROFITABILITY.SALARY_PAGE.TABLE.EMPLOYEE')}</th>
                  <th className="text-end">{t('PROFITABILITY.SALARY_PAGE.TABLE.BASE_SALARY')}</th>
                  <th className="text-end">{t('PROFITABILITY.SALARY_PAGE.TABLE.BONUS')}</th>
                  <th>{t('PROFITABILITY.SALARY_PAGE.TABLE.BONUS_NOTE')}</th>
                  <th className="text-end">{t('PROFITABILITY.SALARY_PAGE.TABLE.TOTAL')}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => {
                  const ed = editRows[r.employeeId] || {};
                  const total = parseFloat(ed.baseSalary || 0) + parseFloat(ed.bonusAmount || 0);
                  return (
                    <tr key={r.employeeId}>
                      <td>
                        {r.firstName} {r.lastName}
                        {r.isFromPreviousMonth ? (
                          <span className="badge bg-warning ms-2 small">{t('PROFITABILITY.SALARY_PAGE.BADGE_PREV_MONTH')}</span>
                        ) : null}
                      </td>
                      <td>
                        <Input
                          type="number" bsSize="sm"
                          className="text-end"
                          style={{ width: 110 }}
                          value={ed.baseSalary || ''}
                          onChange={e => handleChange(r.employeeId, 'baseSalary', e.target.value)}
                        />
                      </td>
                      <td>
                        <Input
                          type="number" bsSize="sm"
                          className="text-end"
                          style={{ width: 110 }}
                          value={ed.bonusAmount || ''}
                          onChange={e => handleChange(r.employeeId, 'bonusAmount', e.target.value)}
                        />
                      </td>
                      <td>
                        <Input
                          type="text" bsSize="sm"
                          placeholder={t('PROFITABILITY.SALARY_PAGE.BONUS_NOTE_PLACEHOLDER')}
                          value={ed.bonusNote || ''}
                          onChange={e => handleChange(r.employeeId, 'bonusNote', e.target.value)}
                        />
                      </td>
                      <td className="text-end fw-bold">{fmt(total)} ₪</td>
                      <td></td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="table-secondary fw-bold">
                  <td>{t('PROFITABILITY.SALARY_PAGE.FOOTER.TOTAL_SALARY')}</td>
                  <td></td><td></td><td></td>
                  <td className="text-end">{fmt(totalSalary)} ₪</td>
                  <td></td>
                </tr>
              </tfoot>
            </Table>
          </div>

          <div className="button-items">
            <Button color="primary" onClick={handleSaveAll} disabled={saving}>
              {saving ? t('PROFITABILITY.SALARY_PAGE.SAVING_BUTTON') : (
                <><i className="ri-save-line me-1"></i>{t('PROFITABILITY.SALARY_PAGE.SAVE_BUTTON')}</>
              )}
            </Button>
          </div>
        </>
      )}
    </React.Fragment>
  );
};

export default EmployeeSalary;
